'use client';

import { c as _c } from "react/compiler-runtime";
import SvgManual from '@common/icon/SvgManual';
import { cn } from '@core/utils';
import type { DetailHomeInstitutionResponse, ListDonationBusinessResponse, ListHomeGiftOrderProductReviewResponse, ListHomeInstitutionContentResponse } from '@generated/unauth';
import type { GiftProductList } from '@/api/getGiftProductList';
import { CustomLink } from '@/app/_components/CustomLink';
import { DONATION_DESKTOP_STICKY_TOP } from '@/app/donation/constants';
import { PageNavigation } from '@/components/client';
import { DONATION_ROUTES } from '@/constants';
import { BREAKPOINTS } from '@/constants/breakpoints';
import type { WEBVIEW_TYPE_ENTRY } from '@/constants/entry';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useEventListTotalCount } from '../_hooks/useEventListTotalCount';
import { HOMETOWNS_DETAIL_TAB } from '../constants';
import { AppBack } from './AppBack';
import { BenefitsContent } from './BenefitsContent';
import { ContentsContent } from './ContentsContent';
import { EmptyState } from './EmptyState';
import { GiftsContent } from './GiftsContent';
import { HometownsTab } from './HometownsTab';
import { MainInfoSection } from './MainInfoSection';
import { PolicyContent } from './PolicyContent';
import { ProjectsContent } from './ProjectsContent';
import { RegionHowToDonate } from './RegionHowToDonate';
import { ReviewsContent } from './ReviewsContent';
import { Section } from './Section';
const SECTION_CLASS_NAME = cn('mb-80 px-40 content-container', 'mobile-tablet:mb-20 mobile-tablet:px-20');
interface HometownsDetailTemplateProps {
  institutionId: number;
  institutionData: DetailHomeInstitutionResponse;
  giftProductData?: GiftProductList;
  donationBusinessData?: ListDonationBusinessResponse;
  institutionContentData?: ListHomeInstitutionContentResponse;
  hasPolicy: boolean;
  giftReviewData?: ListHomeGiftOrderProductReviewResponse;
  isKakaobank: boolean;
  hasGoodsEventBanner: boolean;
  entry: WEBVIEW_TYPE_ENTRY;
}
export const HometownsDetailTemplate = t0 => {
  const $ = _c(58);
  const {
    institutionId,
    institutionData,
    giftProductData,
    donationBusinessData,
    institutionContentData,
    hasPolicy,
    giftReviewData,
    isKakaobank,
    hasGoodsEventBanner,
    entry
  } = t0;
  const t1 = !isKakaobank;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      enabled: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    data: benefitsCount,
    isPending: isBenefitsPending
  } = useEventListTotalCount(institutionId, t2);
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const hasGifts = (giftProductData?.totalCount ?? 0) > 0;
  const hasProjects = (donationBusinessData?.totalCount ?? 0) > 0;
  const hasContents = (institutionContentData?.totalCount ?? 0) > 0;
  const hasReviews = (giftReviewData?.totalCount ?? 0) > 0;
  const hasBenefits = !isKakaobank && (benefitsCount ?? 0) > 0;
  let t3;
  if ($[2] !== entry || $[3] !== giftProductData || $[4] !== hasGifts || $[5] !== institutionId) {
    t3 = hasGifts && giftProductData ? {
      tab: HOMETOWNS_DETAIL_TAB.GIFTS,
      title: "\uB2F5\uB840\uD488",
      content: <GiftsContent institutionId={institutionId} initialData={giftProductData.contents} totalCount={giftProductData.totalCount ?? 0} entry={entry} />
    } : null;
    $[2] = entry;
    $[3] = giftProductData;
    $[4] = hasGifts;
    $[5] = institutionId;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== donationBusinessData || $[8] !== hasProjects || $[9] !== institutionId) {
    t4 = hasProjects && donationBusinessData ? {
      tab: HOMETOWNS_DETAIL_TAB.PROJECTS,
      title: "\uC9C0\uC815\uAE30\uBD80",
      content: <ProjectsContent institutionId={institutionId} initialData={donationBusinessData.contents} />
    } : null;
    $[7] = donationBusinessData;
    $[8] = hasProjects;
    $[9] = institutionId;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== hasContents || $[12] !== institutionContentData || $[13] !== institutionData.slugId || $[14] !== institutionId) {
    t5 = hasContents && institutionContentData ? {
      tab: HOMETOWNS_DETAIL_TAB.CONTENTS,
      title: "\uCF58\uD150\uCE20",
      content: <ContentsContent institutionId={institutionId} institutionSlugId={institutionData.slugId} initialData={institutionContentData.contents} />
    } : null;
    $[11] = hasContents;
    $[12] = institutionContentData;
    $[13] = institutionData.slugId;
    $[14] = institutionId;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== hasPolicy || $[17] !== institutionId) {
    t6 = hasPolicy ? {
      tab: HOMETOWNS_DETAIL_TAB.POLICY,
      title: "\uC815\uCC45",
      content: <PolicyContent institutionId={institutionId} />
    } : null;
    $[16] = hasPolicy;
    $[17] = institutionId;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== giftReviewData || $[20] !== hasReviews || $[21] !== institutionId) {
    t7 = hasReviews && giftReviewData ? {
      tab: HOMETOWNS_DETAIL_TAB.REVIEWS,
      title: "\uD6C4\uAE30",
      content: <ReviewsContent institutionId={institutionId} initialData={giftReviewData.contents} />
    } : null;
    $[19] = giftReviewData;
    $[20] = hasReviews;
    $[21] = institutionId;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== hasBenefits || $[24] !== institutionId) {
    t8 = hasBenefits ? {
      tab: HOMETOWNS_DETAIL_TAB.BENEFITS,
      title: "\uD61C\uD0DD",
      content: <BenefitsContent institutionId={institutionId} />
    } : null;
    $[23] = hasBenefits;
    $[24] = institutionId;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== t3 || $[27] !== t4 || $[28] !== t5 || $[29] !== t6 || $[30] !== t7 || $[31] !== t8) {
    t9 = [t3, t4, t5, t6, t7, t8].flatMap(_temp);
    $[26] = t3;
    $[27] = t4;
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  const sections = t9;
  const hasNoSectionToShow = sections.length === 0 && (isKakaobank || !isBenefitsPending);
  let t10;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <AppBack />;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <PageNavigation donationHomeButton={true} isNotUsedHomeButton={true}><CustomLink href={DONATION_ROUTES.GUIDE.pathname} aria-label={"\uAC00\uC774\uB4DC \uD398\uC774\uC9C0\uB85C \uC774\uB3D9"}><SvgManual className="size-24 text-Gray-900" /></CustomLink></PageNavigation>;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== hasGoodsEventBanner || $[36] !== institutionData.backgroundVideoImageUrl || $[37] !== institutionData.description || $[38] !== institutionData.institutionName || $[39] !== institutionData.logoUrl || $[40] !== institutionData.pcBackgroundVideoImageUrl || $[41] !== institutionData.summary || $[42] !== institutionId || $[43] !== isKakaobank) {
    t12 = <MainInfoSection title={institutionData.institutionName} summary={institutionData.summary} description={institutionData.description} institutionId={institutionId} backgroundVideoImageUrl={institutionData.backgroundVideoImageUrl} pcBackgroundVideoImageUrl={institutionData.pcBackgroundVideoImageUrl} logoUrl={institutionData.logoUrl} isKakaobank={isKakaobank} hasGoodsEventBanner={hasGoodsEventBanner} />;
    $[35] = hasGoodsEventBanner;
    $[36] = institutionData.backgroundVideoImageUrl;
    $[37] = institutionData.description;
    $[38] = institutionData.institutionName;
    $[39] = institutionData.logoUrl;
    $[40] = institutionData.pcBackgroundVideoImageUrl;
    $[41] = institutionData.summary;
    $[42] = institutionId;
    $[43] = isKakaobank;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  let t13;
  if ($[45] !== isDesktopViewport || $[46] !== sections) {
    t13 = sections.length > 0 ? <><header className={cn("sticky z-10 mt-40 px-40 content-container", "mobile-tablet:top-48 mobile-tablet:mt-20 mobile-tablet:px-0")} style={isDesktopViewport ? {
        top: DONATION_DESKTOP_STICKY_TOP
      } : undefined}><HometownsTab tabs={sections.map(_temp2)} /></header>{sections.map(_temp3)}</> : null;
    $[45] = isDesktopViewport;
    $[46] = sections;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  let t14;
  if ($[48] !== hasNoSectionToShow) {
    t14 = hasNoSectionToShow ? <EmptyState className={SECTION_CLASS_NAME} /> : null;
    $[48] = hasNoSectionToShow;
    $[49] = t14;
  } else {
    t14 = $[49];
  }
  let t15;
  if ($[50] !== institutionData.institutionName || $[51] !== isKakaobank) {
    t15 = isKakaobank ? null : <RegionHowToDonate institutionName={institutionData.institutionName} className={SECTION_CLASS_NAME} />;
    $[50] = institutionData.institutionName;
    $[51] = isKakaobank;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  let t16;
  if ($[53] !== t12 || $[54] !== t13 || $[55] !== t14 || $[56] !== t15) {
    t16 = <div className="relative">{t10}{t11}{t12}{t13}{t14}{t15}</div>;
    $[53] = t12;
    $[54] = t13;
    $[55] = t14;
    $[56] = t15;
    $[57] = t16;
  } else {
    t16 = $[57];
  }
  return t16;
};
function _temp(section) {
  return section ? [section] : [];
}
function _temp2(t0) {
  const {
    tab,
    title
  } = t0;
  return {
    tab,
    title
  };
}
function _temp3(t0) {
  const {
    tab: tab_0,
    content
  } = t0;
  return <Section key={tab_0} id={tab_0} className={SECTION_CLASS_NAME}>{content}</Section>;
}